/**
 * Draft service — CRUD operations for figgy_drafts table.
 */

import { supabase } from '@/integrations/supabase/client';
import type {
  FiggyDraft,
  CreateDraftInput,
  DraftPublicView,
  DraftStatus,
  DraftUpdateInput,
} from '@/types/draft';
import { DRAFT_UPDATE_KEYS } from '@/types/draft';

// ── Category-based defaults ────────────────────────────────────────

const CATEGORY_DEFAULTS: Record<string, {
  bio: string;
  hero_headline: string;
  cta_label: string;
  services: Array<{ name: string; duration: number; price: number }>;
}> = {
  barber: {
    bio: 'Precision cuts and clean fades. Walk out looking sharp.',
    hero_headline: 'Your look, elevated.',
    cta_label: 'Book a cut',
    services: [
      { name: 'Haircut', duration: 30, price: 35 },
      { name: 'Haircut + Beard', duration: 45, price: 50 },
      { name: 'Beard Trim', duration: 20, price: 20 },
    ],
  },
  stylist: {
    bio: 'Personalized styling that brings out your best.',
    hero_headline: 'Style that speaks for you.',
    cta_label: 'Book an appointment',
    services: [
      { name: 'Blowout', duration: 45, price: 55 },
      { name: 'Cut & Style', duration: 60, price: 75 },
      { name: 'Color', duration: 90, price: 120 },
    ],
  },
  'nail tech': {
    bio: 'Clean, creative nail art and self-care you deserve.',
    hero_headline: 'Nails that make a statement.',
    cta_label: 'Book your nails',
    services: [
      { name: 'Gel Manicure', duration: 45, price: 45 },
      { name: 'Full Set Acrylic', duration: 75, price: 65 },
      { name: 'Pedicure', duration: 50, price: 50 },
    ],
  },
  'lash tech': {
    bio: 'Lash extensions that enhance your natural beauty.',
    hero_headline: 'Wake up flawless.',
    cta_label: 'Book your lashes',
    services: [
      { name: 'Classic Full Set', duration: 90, price: 120 },
      { name: 'Volume Full Set', duration: 120, price: 160 },
      { name: 'Fill', duration: 60, price: 65 },
    ],
  },
  trainer: {
    bio: 'Results-driven training built around your goals.',
    hero_headline: 'Train smarter. Look better.',
    cta_label: 'Book a session',
    services: [
      { name: '1-on-1 Session', duration: 60, price: 80 },
      { name: 'Assessment', duration: 45, price: 50 },
    ],
  },
  tattoo: {
    bio: 'Custom tattoos and fine line work.',
    hero_headline: 'Art that lives with you.',
    cta_label: 'Book a consultation',
    services: [
      { name: 'Consultation', duration: 30, price: 0 },
      { name: 'Small Tattoo', duration: 60, price: 150 },
      { name: 'Large Tattoo', duration: 180, price: 400 },
    ],
  },
};

const DEFAULT_CATEGORY = {
  bio: 'Premium service, personalized for you.',
  hero_headline: 'Your experience, elevated.',
  cta_label: 'Book now',
  services: [] as Array<{ name: string; duration: number; price: number }>,
};

// ── Create ─────────────────────────────────────────────────────────

export async function createDraftFromLead(input: CreateDraftInput): Promise<FiggyDraft> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const categoryKey = (input.category || '').toLowerCase();
  const defaults = CATEGORY_DEFAULTS[categoryKey] || DEFAULT_CATEGORY;

  const instagramUrl = input.instagram_handle
    ? `https://instagram.com/${input.instagram_handle.replace(/^@/, '')}`
    : null;

  const services = defaults.services.map((s, i) => ({
    id: crypto.randomUUID(),
    name: s.name,
    duration: s.duration,
    price: s.price,
  }));

  const row = {
    created_by: user.id,
    business_name: input.business_name || null,
    provider_name: input.provider_name || null,
    category: input.category || null,
    city: input.city || null,
    instagram_handle: input.instagram_handle?.replace(/^@/, '') || null,
    instagram_url: instagramUrl,
    website_url: input.website_url || null,
    booking_url: input.booking_url || null,
    phone: input.phone || null,
    notes: input.notes || null,
    bio: defaults.bio,
    hero_headline: defaults.hero_headline,
    cta_label: defaults.cta_label,
    services,
    custom_links: [],
  };

  const { data, error } = await supabase
    .from('figgy_drafts')
    .insert(row)
    .select()
    .single();

  if (error) throw error;
  return data as unknown as FiggyDraft;
}

// ── Update ─────────────────────────────────────────────────────────

/** Pick only operator-editable draft fields for the editor update path. */
export function pickDraftUpdate(
  patch: Partial<FiggyDraft> | DraftUpdateInput,
): DraftUpdateInput {
  const out: DraftUpdateInput = {};
  for (const key of DRAFT_UPDATE_KEYS) {
    if (Object.prototype.hasOwnProperty.call(patch, key)) {
      (out as Record<string, unknown>)[key] = (patch as Record<string, unknown>)[key];
    }
  }
  return out;
}

export async function updateDraft(
  draftId: string,
  patch: Partial<FiggyDraft> | DraftUpdateInput,
): Promise<FiggyDraft> {
  const safePatch = pickDraftUpdate(patch);

  const { data, error } = await supabase
    .from('figgy_drafts')
    .update(safePatch)
    .eq('id', draftId)
    .select()
    .single();

  if (error) throw error;
  return data as unknown as FiggyDraft;
}

// ── Read (admin) ───────────────────────────────────────────────────

export async function getDraftById(draftId: string): Promise<FiggyDraft | null> {
  const { data, error } = await supabase
    .from('figgy_drafts')
    .select('*')
    .eq('id', draftId)
    .maybeSingle();

  if (error) throw error;
  return data as unknown as FiggyDraft | null;
}

export async function listDrafts(): Promise<FiggyDraft[]> {
  const { data, error } = await supabase
    .from('figgy_drafts')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data || []) as unknown as FiggyDraft[];
}

// ── Read (public preview via RPC) ──────────────────────────────────

export async function getDraftByPreviewToken(
  token: string,
): Promise<DraftPublicView | null> {
  const { data, error } = await supabase.rpc('get_draft_preview', {
    p_token: token,
  });

  if (error) throw error;
  return data as DraftPublicView | null;
}

// ── Status update helpers ──────────────────────────────────────────

export async function updateDraftStatus(
  draftId: string,
  status: DraftStatus,
): Promise<void> {
  const { error } = await supabase
    .from('figgy_drafts')
    .update({ status })
    .eq('id', draftId);

  if (error) throw error;
}

// Phase 1 does not expose draft → business conversion in the UI.
// Do not add client-side multi-write conversion here — when claim ships,
// implement it as a single transactional SECURITY DEFINER RPC.
